// ADDED: Stimulus controller for signup form validation and interactivity
// PRD Reference: docs/PRD_SIGNUP.md - Stimulus Form Controller
// Tasks 7.1-7.6: Form validation, password toggle, phone formatting, loading state

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  // Task 7.1: Define targets for form elements
  static targets = [
    'firstName',
    'lastName',
    'email',
    'emailError',
    'password',
    'passwordIcon',
    'passwordHint',
    'phone',
    'terms',
    'submitButton'
  ]

  // Track password visibility state
  passwordVisible = false

  connect() {
    // Initialize form state
    console.log('Signup form controller connected')
  }

  // Task 7.2: Real-time email format validation
  validateEmail() {
    if (!this.hasEmailTarget || !this.hasEmailErrorTarget) return

    const email = this.emailTarget.value
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (email && !emailRegex.test(email)) {
      this.emailTarget.classList.add('form-input-error')
      this.emailErrorTarget.textContent = 'Please enter a valid email address'
      this.emailErrorTarget.classList.add('form-error')
    } else {
      this.emailTarget.classList.remove('form-input-error')
      this.emailErrorTarget.textContent = ''
      this.emailErrorTarget.classList.remove('form-error')
    }
  }

  // Task 7.3: Password validation and show/hide toggle
  validatePassword() {
    if (!this.hasPasswordTarget || !this.hasPasswordHintTarget) return

    const password = this.passwordTarget.value
    const minLength = 6

    if (password && password.length < minLength) {
      this.passwordTarget.classList.add('form-input-error')
      this.passwordHintTarget.textContent = `Password must be at least ${minLength} characters`
      this.passwordHintTarget.classList.add('form-error')
    } else if (password && password.length >= minLength) {
      this.passwordTarget.classList.remove('form-input-error')
      this.passwordHintTarget.textContent = 'Password strength: Good'
      this.passwordHintTarget.classList.remove('form-error')
      this.passwordHintTarget.classList.add('text-green-600')
    } else {
      this.passwordTarget.classList.remove('form-input-error')
      this.passwordHintTarget.textContent = 'Must be at least 6 characters'
      this.passwordHintTarget.classList.remove('form-error', 'text-green-600')
    }
  }

  togglePassword() {
    if (!this.hasPasswordTarget || !this.hasPasswordIconTarget) return

    this.passwordVisible = !this.passwordVisible

    if (this.passwordVisible) {
      this.passwordTarget.type = 'text'
      // Show "hide" icon (eye with slash)
      this.passwordIconTarget.innerHTML = `
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
      `
    } else {
      this.passwordTarget.type = 'password'
      // Show "show" icon (eye)
      this.passwordIconTarget.innerHTML = `
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
      `
    }
  }

  // Task 7.4: Phone number input formatting (US format)
  formatPhone() {
    if (!this.hasPhoneTarget) return

    let phone = this.phoneTarget.value.replace(/\D/g, '')

    // Limit to 10 digits
    phone = phone.substring(0, 10)

    // Format as (XXX) XXX-XXXX
    if (phone.length >= 6) {
      phone = `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`
    } else if (phone.length >= 3) {
      phone = `(${phone.substring(0, 3)}) ${phone.substring(3)}`
    } else if (phone.length > 0) {
      phone = `(${phone}`
    }

    this.phoneTarget.value = phone
  }

  // Task 7.5: Form submission loading state
  handleSubmit(event) {
    // Task 7.6: Validate terms checkbox
    if (this.hasTermsTarget && !this.termsTarget.checked) {
      event.preventDefault()
      alert('Please accept the Terms of Service and Privacy Policy to continue.')
      return
    }

    // Show loading state
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Creating account...
      `
      this.submitButtonTarget.classList.add('btn-loading')
    }
  }
}
